import DailyDealsIcon from 'assets/svg/daily-deals/daily-deals.svg'
import Badge from 'common/components/Badge'
import Divider from 'common/components/Divider'
import Image from 'common/components/Image'
import Progress from 'common/components/Progress'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import CurrencyFormatter from 'lib/currency-formatter'
import styled from 'styled-components'

const Container = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 6px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.11);
  height: ${({ height }) => height || '340px'};
  width: ${({ width }) => width || '220px'};
`

const ImageContainer = styled.div`
  position: relative;
  width: ${({ width }) => width || '208px'};
  height: ${({ imageHeight }) => imageHeight};
  padding: 6px 6px 2px 6px;
`

const DividerContainer = styled.div`
  margin-top: 0px;
`

const BadgeContainer = styled.div`
  position: absolute;
  margin: 7px 7px 0 138px;
  visibility: ${({ discount }) =>
    discount && discount !== '0%' ? 'display' : 'hidden'};
`

const ProductDetailContainer = styled.div`
  margin: 20px 20px 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  height: 36px;
  color: ${theme.colors.black};
`

const CurrentPriceContainer = styled.div`
  margin: 12px 101px 9px 20px;
`

const PreviousPriceContainer = styled.div`
  margin: 5px 20px 0px 20px;
  display: inline-flex;
  visibility: ${({ previousPrice, currentPrice }) =>
    currentPrice && previousPrice !== currentPrice
      ? 'display'
      : 'hidden'};
`

const PreviousPrice = styled.label`
  width: max-content;
  text-decoration: line-through;
  text-decoration-color: gray;
  margin-right: 5px;
`

const DiscountContainer = styled.div`
  visibility: ${({ discount }) =>
    discount && discount !== '0%' ? 'display' : 'hidden'};
`

const ProgressContainer = styled.div`
  margin: 20px 20px 9px;
`

const AvailableProductContainer = styled.div`
  display: inline-flex;
  margin: 5px 7px 0 20px;
`

interface IProductDailyDealsCard {
  height?: string
  width?: string
  image: string
  imageHeight?: string
  name: string
  currentPrice: string
  previousPrice?: string
  availableProduct: number
  totalProduct: number
  productURL: string
  onClickProduct?: any
  percentage?: string
  maskedDiscount?: string
  maskedPrice?: string
}

function ProductDailyDealsCard({
  height,
  width,
  image,
  imageHeight,
  name,
  currentPrice,
  previousPrice,
  availableProduct,
  totalProduct,
  productURL,
  onClickProduct,
  percentage,
  maskedDiscount,
  maskedPrice,
}: IProductDailyDealsCard): JSX.Element {
  const soldOutPercent =
    availableProduct !== null && totalProduct !== null
      ? ((totalProduct - availableProduct) / totalProduct) * 100
      : 0

  const remainingItems = () => {
    switch (true) {
      case soldOutPercent === 0:
        return `${availableProduct} Tersedia`
      case soldOutPercent > 0 && soldOutPercent <= 90:
        return 'Terjual Sebagian'
      case soldOutPercent > 90 && soldOutPercent < 100:
        return 'Hampir Habis'
      default:
        return 'Habis Terjual'
    }
  }

  return (
    <a href={productURL} target="#" onClick={onClickProduct}>
      <Container
        height={height}
        width={width}
        data-testid="product-daily-deals-card-component"
      >
        <ImageContainer>
          <BadgeContainer discount={percentage}>
            <Badge variant="alt1" content={`${percentage} OFF`} />
          </BadgeContainer>
          <Image
            lazy
            src={image}
            height={imageHeight || '150px'}
            borderTopLeftRadius="6px"
            borderTopRightRadius="6px"
            objectFit="contain"
          />
        </ImageContainer>
        <DividerContainer>
          <Divider
            color={theme.colors.grayLight}
            width="100%"
            height="1px"
            borderRadius="0px"
          />
        </DividerContainer>
        <ProductDetailContainer>
          <Typography fontSize="14px" color={theme.colors.black}>
            {name}
          </Typography>
        </ProductDetailContainer>

        {maskedPrice && (
          <CurrentPriceContainer>
            <Typography
              fontSize="16px"
              fontWeight="bold"
              color="orange"
            >
              {maskedPrice}
            </Typography>
          </CurrentPriceContainer>
        )}

        {maskedPrice && (
          <PreviousPriceContainer
            previousPrice={maskedPrice}
            currentPrice={
              maskedDiscount && percentage && percentage !== '0%'
                ? '-'
                : maskedPrice
            }
          >
            <PreviousPrice>
              <Typography fontSize="10px" color="gray">
                {maskedDiscount}
              </Typography>
            </PreviousPrice>
            <DiscountContainer discount={percentage}>
              <Badge
                content={percentage}
                backgroundColor={theme.colors.redLighter}
                fontColor={theme.colors.red}
                borderRadius="4px"
                position="static"
                height="14px"
                fontSize="8px"
              />
            </DiscountContainer>
          </PreviousPriceContainer>
        )}
        {!maskedPrice && (
          <CurrentPriceContainer>
            <Typography
              fontSize="16px"
              fontWeight="bold"
              color="orange"
            >
              {currentPrice
                ? CurrencyFormatter(parseFloat(currentPrice))
                : CurrencyFormatter(parseFloat(previousPrice))}
            </Typography>
          </CurrentPriceContainer>
        )}
        {!maskedPrice && (
          <PreviousPriceContainer
            previousPrice={previousPrice}
            currentPrice={currentPrice}
          >
            <PreviousPrice>
              <Typography fontSize="13px" color="gray">
                {previousPrice
                  ? CurrencyFormatter(parseFloat(previousPrice))
                  : 'Rp 0'}
              </Typography>
            </PreviousPrice>
            <DiscountContainer discount={percentage}>
              <Badge
                content={percentage}
                backgroundColor={theme.colors.redLighter}
                fontColor="red"
                borderRadius="6px"
                position="static"
                width="27px"
                height="18px"
                fontSize="9px"
                fontWeight="normal"
              />
            </DiscountContainer>
          </PreviousPriceContainer>
        )}
        <ProgressContainer>
          <Progress width="180px" percent={soldOutPercent} />
        </ProgressContainer>
        <AvailableProductContainer>
          <DailyDealsIcon />
          <Typography
            fontSize="13px"
            color="grayDark"
            marginLeft="7px"
          >
            {remainingItems()}
          </Typography>
        </AvailableProductContainer>
      </Container>
    </a>
  )
}

export default ProductDailyDealsCard
